import type { AnimationItem } from 'lottie-web';
import { equals } from 'ramda';
import { CSSProperties, memo, useEffect, useMemo, useRef } from 'react';

export interface LottieAnimationProps {
  /**
   * Path to the Lottie animation JSON file.
   */
  animation: string;

  /**
   * Whether the animation should loop or play just once.
   * @default true
   */
  loop?: boolean;

  /**
   * Width of the animation container.
   */
  width?: number;

  /**
   * Height of the animation container.
   */
  height?: number;

  /**
   * Callback function to be called when the animation completes.
   * Does not trigger if the animation is set to loop.
   */
  onAnimationFinished?: () => void;

  /**
   * Additional className for the container wrapper.
   */
  className?: string;
}

interface Props extends LottieAnimationProps {
  /**
   * Positioning style for the outer Box
   */
  style?: CSSProperties;
}

/**
 * Renders a Lottie animation using the lottie-web library inside a div with a passed style.
 */
const LottieAnimationComp = ({
  animation,
  loop = true,
  onAnimationFinished,
  width,
  height,
  style: propsStyle,
  className,
}: Props) => {
  // Ref for the inner div where Lottie actually renders
  const animationContainer = useRef<HTMLDivElement>(null);
  // Ref to store the Lottie player instance
  const playerRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    const { current: wrapperEl } = animationContainer;
    if (!wrapperEl) return;

    (async () => {
      const lottie = (await import('lottie-web')).default;

      // Store instance in ref
      playerRef.current = lottie.loadAnimation({
        container: wrapperEl, // Target inner div
        renderer: 'svg',
        loop,
        autoplay: true,
        path: animation,
      });

      // Add event listener if callback exists
      if (onAnimationFinished) {
        playerRef.current.addEventListener('complete', onAnimationFinished);
      }
    })();

    // Cleanup function
    return () => {
      // Remove listener first if it was added
      if (onAnimationFinished && playerRef.current) {
        playerRef.current.removeEventListener('complete', onAnimationFinished);
      }
      // Destroy animation and clear ref
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [animationContainer, animation, loop, onAnimationFinished]);

  const style = useMemo(
    () => ({
      width,
      height,
      ...propsStyle,
    }),
    [height, propsStyle, width],
  );

  return <div style={style} className={className} ref={animationContainer} />;
};

export const LottieAnimation = memo(LottieAnimationComp, equals);
